/**
 * The vegetables and the other plain foods (spec 02 §7.3): eggplant, egg, turnip, onion, carrot,
 * boxnut (a chestnut, after the C64's brown pointed nut), beet, corn. The long ones lie diagonal,
 * as the C64 draws them, and rest on their lowest point.
 */

import { Vector3 } from 'three';
import { type MeshBuilder, place } from '../../kit/MeshBuilder';
import type { Palette } from '../../palette';
import { arcPts, type Builder, chain, lathe, leaf, rotZ, scaled, tube } from './kit';

type Pt = readonly [number, number];

type Blade = readonly [number, number, number];

/** Leaves fanned up from (x, y): [roll, length, yaw] each. */
function tuft(mb: MeshBuilder, p: Palette, x: number, y: number, leaves: readonly Blade[], width = 0.16): void {
  leaves.forEach(([roll, len, yaw], i) => {
    leaf(mb, p.color(i % 2 ? 'leaf.1' : 'leaf.2'), x, y, 0, len, width, roll, yaw);
  });
}

export const VEG: Record<string, Builder> = {
  eggplant: (mb, p) => {
    // A glossy purple teardrop lying diagonal, big end down, under a green cap and stem.
    const prof: Pt[] = [...arcPts(0, 0.3, 0.3, 0.3, -Math.PI / 2, 0, 5), [0.29, 0.42], [0.25, 0.56], [0.2, 0.68],
      [0.17, 0.78], [0.12, 0.86], [0, 0.88]];
    mb.with(chain(rotZ(-0.16, 0.09, -0.75), place(0, 0, 0, 0, 1.14)), () => {
      lathe(mb, prof.map(([r, y]) => [Math.max(0, r), y] as const), p.color('eggplant.1'), undefined, { sides: 20 });
      mb.blob(0.05, 0.16, 0.03, p.color('eggplant.3'), rotZ(-0.14, 0.4, 0.25, 0.27), { detail: 1 });
      lathe(mb, [[0.19, 0.7], [0.2, 0.76], [0.14, 0.86], [0.07, 0.92], [0, 0.93]], p.color('leaf.1'), undefined,
        { sides: 16 });
      for (let i = 0; i < 5; i++) {
        leaf(mb, p.color('leaf.2'), 0, 0.84, 0, 0.18, 0.12, Math.PI - 0.5, (i / 5) * Math.PI * 2);
      }
      tube(mb, [new Vector3(0, 0.9, 0), new Vector3(0.01, 1.02, 0), new Vector3(0.05, 1.08, 0)], [0.045, 0.04, 0.035],
        p.color('leaf.0'), 6);
    });
  },
  egg: (mb, p) => {
    const prof: Pt[] = [];
    for (let i = 0; i <= 14; i++) {
      const a = -Math.PI / 2 + (Math.PI * i) / 14;
      prof.push([Math.max(0, 0.35 * Math.cos(a) * (1 - 0.14 * Math.sin(a))), 0.45 + 0.45 * Math.sin(a)]);
    }
    lathe(mb, prof, (seg) => p.color(seg > 10 ? 'egg.2' : 'egg.1'), undefined, { sides: 24 });
  },
  turnip: (mb, p) => {
    // A white root with a purple top and a thin tail, under three big leaves.
    const prof: Pt[] = [[0, 0], [0.03, 0.06], [0.1, 0.13], ...arcPts(0, 0.42, 0.37, 0.32, -1.1, 1.25, 8)
      .map(([r, y]) => [Math.max(0, r), y] as const), [0.08, 0.76], [0, 0.77]];
    lathe(mb, prof, (seg) => p.color(seg >= 7 ? 'turnip.1' : seg === 6 ? 'turnip.2' : 'egg.1'), undefined,
      { sides: 22 });
    tuft(mb, p, 0, 0.72, [[0, 0.4, 0], [0.6, 0.4, 0.4], [-0.6, 0.4, -0.4]], 0.24);
  },
  onion: (mb, p) => {
    // A golden bulb with a pointed neck, darker stripes from root to tip, and root hairs.
    const prof: Pt[] = [[0, 0.05], [0.14, 0.04], ...arcPts(0, 0.36, 0.4, 0.32, -1.1, 0.6, 7)
      .map(([r, y]) => [Math.max(0, r), y] as const), [0.18, 0.72], [0.07, 0.84], [0.035, 0.94], [0, 0.96]];
    lathe(mb, prof, (_, k) => p.color(k % 3 === 0 ? 'onion.1' : k % 3 === 1 ? 'onion.2' : 'onion.2'), undefined,
      { sides: 18 });
    for (const a of [-1.2, -0.6, 0, 0.6, 1.2]) {
      const [x, z] = [Math.sin(a), Math.cos(a)];
      tube(mb, [new Vector3(x * 0.06, 0.06, z * 0.06), new Vector3(x * 0.14, 0.015, z * 0.14)],
        [0.02, 0.015], p.color('egg.1'), 5);
    }
    mb.blob(0.035, 0.05, 0.035, p.color('onion.0'), place(0, 0.96, 0), { detail: 1 });
  },
  carrot: (mb, p) => {
    // An orange cone, point down-left, a fan of leaves up-right, faint rings across it.
    const prof: Pt[] = [[0, 0], [0.05, 0.1], [0.1, 0.25], [0.14, 0.4], [0.17, 0.55], [0.2, 0.7], [0.21, 0.78],
      [0.17, 0.84], [0, 0.86]];
    mb.with(chain(rotZ(-0.36, 0, -0.72), place(0, 0, 0, 0, 1.14)), () => {
      lathe(mb, prof, (seg) => p.color(seg === 2 || seg === 4 ? 'carrot.1' : seg > 5 ? 'carrot.3' : 'carrot.2'),
        undefined, { sides: 16 });
      tuft(mb, p, 0, 0.82, [[0, 0.34, 0], [0.4, 0.3, 0.5], [-0.4, 0.3, -0.5]], 0.14);
    });
  },
  boxnut: (mb, p) => {
    // A chestnut: glossy brown, pointed at the top, its pale rough base underneath.
    const prof: Pt[] = [[0, 0.02], [0.22, 0], [0.36, 0.07], [0.44, 0.2], [0.45, 0.34], [0.4, 0.5], [0.3, 0.64],
      [0.16, 0.78], [0.05, 0.88], [0, 0.9]];
    lathe(mb, prof, (seg) => p.color(seg < 2 ? 'chestnut.3' : seg > 6 ? 'chestnut.2' : 'chestnut.1'),
      scaled(0, 0, 0, 1, 1, 0.72), { sides: 22 });
    mb.blob(0.04, 0.13, 0.02, p.color('chestnut.2'), rotZ(-0.2, 0.5, 0.35, 0.31), { detail: 1 });
    mb.blob(0.03, 0.05, 0.03, p.color('chestnut.3'), place(0, 0.92, 0), { detail: 1 });
  },
  beet: (mb, p) => {
    // A deep crimson root with a long tail, red stalks and green leaves.
    const prof: Pt[] = [[0, 0], [0.025, 0.06], [0.08, 0.14], ...arcPts(0, 0.4, 0.36, 0.3, -1.2, 1.3, 8)
      .map(([r, y]) => [Math.max(0, r), y] as const), [0.08, 0.72], [0, 0.73]];
    lathe(mb, prof, (seg) => p.color(seg > 8 ? 'beet.2' : 'beet.1'), undefined, { sides: 22 });
    for (const [roll, yaw, len] of [[0.5, 0.3, 0.3], [-0.45, -0.3, 0.34], [0.05, 0.9, 0.26]] as const) {
      const tip = new Vector3(-Math.sin(roll) * 0.18, 0.88, 0);
      tube(mb, [new Vector3(0, 0.68, 0), tip], [0.03, 0.025], p.color('beet.2'), 6);
      leaf(mb, p.color(yaw > 0 ? 'leaf.1' : 'leaf.2'), tip.x, tip.y, tip.z, len, 0.2, roll, yaw);
    }
  },
  corn: (mb, p) => {
    // A cob of yellow kernels (a diamond checker), lying diagonal in its green husk.
    const prof: Pt[] = [[0, 0.02], [0.14, 0.03], [0.19, 0.1], [0.2, 0.25], [0.2, 0.45], [0.19, 0.62], [0.16, 0.76],
      [0.11, 0.86], [0, 0.9]];
    const sides = 14;
    mb.with(chain(rotZ(-0.2, 0.16, -0.8), place(0, 0, 0, 0, 1.2)), () => {
      lathe(mb, prof, (seg, k) => p.color((seg + k) % 2 ? 'corn.1' : 'corn.2'), undefined, {
        sides, twist: prof.map((_, i) => (i * Math.PI) / sides),
      });
      leaf(mb, p.color('husk.1'), 0.04, 0.02, 0.1, 0.62, 0.3, -0.35, 0.2);
      leaf(mb, p.color('husk.2'), -0.04, 0.02, 0.08, 0.56, 0.28, 0.4, -0.2);
      leaf(mb, p.color('husk.0'), 0, 0.02, -0.1, 0.5, 0.3, 0.05, 0);
      tube(mb, [new Vector3(0, 0.04, 0), new Vector3(0, -0.12, 0)], [0.06, 0.05], p.color('husk.0'), 6);
    });
  },
};
